import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import {SocialProfileIcon, SocialProfileIconSize} from '@Components/social-media/components/social-profile-icon';
import {Button, Size, Type} from '@Components/button';
import {SocialProfileIconPlaceholder} from '@Components/social-media-wizard/components/social-profile-icon-placeholder';
import {MAX_ACCOUNT_BUTTONS_TO_SHOW} from '@Libraries/social-media-wizard-library';
import type {SelectedAccountsHeaderProps} from './selected-accounts-header.types';
import styles from './selected-accounts-header.module.scss';

export function SelectedAccountsHeader({
  availableAccounts,
  accountIdsToPublishTo,
  numAccountsToPublishTo,
  onAccountUnselected,
  onDeselectAll,
  isAccountPublishable,
}: SelectedAccountsHeaderProps): ReactElement {
  const getSelectedAccountButtons = (): ReactElement[] => {
    const accountButtons: ReactElement[] = [];
    let hasAppendedPlaceholder = false;
    let numButtonsHidden = 0;

    availableAccounts.forEach((account) => {
      if (accountIdsToPublishTo.includes(account.id) && isAccountPublishable(account.id)) {
        if (accountButtons.length >= MAX_ACCOUNT_BUTTONS_TO_SHOW) {
          numButtonsHidden += 1;
          hasAppendedPlaceholder = true;
        } else {
          accountButtons.push(
            <SocialProfileIcon
              key={`social-profile-icon-${account.id}`}
              className="spacing-m-r-2"
              account={account}
              size={SocialProfileIconSize.LARGE}
              onClick={(): void => {
                onAccountUnselected(account.id);
              }}
            />
          );
        }
      }
    });

    if (hasAppendedPlaceholder) {
      accountButtons.push(<SocialProfileIconPlaceholder key="social-profile-icon-placeholder" countToShow={numButtonsHidden} size={SocialProfileIconSize.LARGE} />);
    }

    return accountButtons;
  };

  const getSelectedAccountsContent = (): ReactElement | null => {
    if (numAccountsToPublishTo === 0) {
      return null;
    }

    const deselectText = numAccountsToPublishTo === 1 ? window.i18next.t('pmwjs_deselect') : window.i18next.t('pmwjs_deselect_all');

    return (
      <>
        <Text val={`${window.i18next.t('pmwjs_selected')} (${numAccountsToPublishTo})`} size={TextSize.XSMALL} bold className={`spacing-m-b-2 ${styles.textElement}`} />
        <div className="flex-h-row flex-content-between">
          <div className={`flex-h-row ${styles.accountButtons}`}>{getSelectedAccountButtons()}</div>
          <Button customClasses={styles.deselectAllBtn} text={deselectText} type={Type.GHOST} size={Size.SMALL} onClick={onDeselectAll} />
        </div>
      </>
    );
  };

  const getAddedAccountsLabel = (): ReactElement | null => {
    if (numAccountsToPublishTo === 0) {
      return null;
    }

    return <Text val={window.i18next.t('pmwjs_added_accounts')} size={TextSize.XSMALL} bold className={`spacing-m-b-1 ${styles.textElement}`} />;
  };

  return (
    <>
      <div className={`${styles.accountButtonsContainer} ${numAccountsToPublishTo === 0 ? styles.noAccounts : ''}`}>{getSelectedAccountsContent()}</div>
      {getAddedAccountsLabel()}
    </>
  );
}
